import React, { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_1e4446e1-36f0-48d6-b8d8-c196b23a6c28/artifacts/r6xve12v_video%20sezione%201.mp4";

const HeroSection = () => {
  const { t } = useLanguage();
  const words = t.hero.words;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
        setVisible(true);
      }, 400);
    }, 2200);
    return () => clearInterval(interval);
  }, [words.length]);

  // Split "Communicate your " → ["Communicate", "your"]
  const parts = t.hero.tagline1.trim().split(" ");
  const taglineWord1 = parts[0];
  const taglineWord2 = parts.slice(1).join(" ").trim();

  return (
    <section
      data-testid="hero-section"
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        minHeight: "580px",
        overflow: "hidden",
      }}
    >
      <video
        data-testid="hero-video"
        autoPlay muted loop playsInline
        preload="auto"
        style={{
          position: "absolute", inset: 0,
          width: "100%", height: "100%",
          objectFit: "cover", zIndex: 0,
        }}
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      <div style={{
        position: "absolute", inset: 0, zIndex: 1,
        background: "linear-gradient(135deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.32) 50%, rgba(0,0,0,0.58) 100%)",
      }} />

      <div style={{
        position: "absolute", inset: 0, zIndex: 2,
        display: "flex",
        alignItems: "center",
        padding: isMobile ? "0 28px" : "0 64px",
      }}>
        <div style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: isMobile ? "100px" : "48px",
          alignItems: "center",
        }}>
          {/* Left column: We Connect + animated word */}
          <div>
            <p style={{
              fontFamily: "'Glacial Indifference', 'Helvetica Neue', Arial, sans-serif",
              fontWeight: 400,
              fontSize: isMobile ? "clamp(32px, 8.5vw, 44px)" : "clamp(42px, 5vw, 66px)",
              color: "#fff",
              margin: "0 0 10px 0",
              letterSpacing: "0.01em",
              lineHeight: 1.1,
              textShadow: "0 2px 12px rgba(0,0,0,0.4)",
              textTransform: "capitalize",
            }}>
              {t.hero.weConnect}.
            </p>
            <div style={{
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              height: isMobile ? "clamp(42px, 10vw, 54px)" : "clamp(54px, 6.5vw, 84px)",
            }}>
              <span style={{
                fontFamily: "'Monotype Corsiva', 'Cormorant Garamond', cursive",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: isMobile ? "clamp(36px, 9.5vw, 50px)" : "clamp(50px, 6.2vw, 78px)",
                color: "#fff",
                letterSpacing: "-1px",
                lineHeight: 1,
                display: "block",
                textShadow: "0 2px 12px rgba(0,0,0,0.4)",
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(22px)",
                transition: "opacity 0.4s ease, transform 0.4s ease",
                textTransform: "lowercase",
              }}>
                {words[currentIndex]}
              </span>
            </div>
          </div>

          {/* Right column: VIDI + multi-line tagline */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}>
            {/* VIDI — 90px on desktop */}
            <div style={{
              fontFamily: "'Archivo Black', 'Arial Black', sans-serif",
              fontWeight: 900,
              fontSize: isMobile ? "clamp(55px, 14vw, 70px)" : "clamp(76px, 6.5vw, 90px)",
              color: "#fff",
              letterSpacing: isMobile ? "-1.5px" : "-3px",
              lineHeight: 0.88,
              textShadow: "0 4px 28px rgba(0,0,0,0.3)",
              marginBottom: isMobile ? "14px" : "18px",
            }}>
              VIDI
            </div>

            {/* Tagline — multi line */}
            <div style={{
              fontFamily: "'Archivo Black', 'Arial Black', sans-serif",
              fontWeight: 900,
              color: "#fff",
              textShadow: "0 2px 8px rgba(0,0,0,0.4)",
              lineHeight: 1.05,
            }}>
              {/* Line 1: "Communicate" */}
              <div style={{
                fontSize: isMobile ? "clamp(26px, 6.5vw, 38px)" : "clamp(55px, 4.2vw, 65px)",
                letterSpacing: "-0.5px",
                marginBottom: "2px",
              }}>
                {taglineWord1}
              </div>
              {/* Line 2: "your" + "identity" */}
              <div style={{
                display: "flex",
                alignItems: "baseline",
                gap: "8px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}>
                <span style={{
                  fontSize: isMobile ? "clamp(26px, 6.5vw, 38px)" : "clamp(55px, 4.2vw, 65px)",
                  letterSpacing: "-0.5px",
                }}>
                  {taglineWord2}
                </span>
                <span style={{
                  fontFamily: "'Monotype Corsiva', 'Cormorant Garamond', cursive",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: isMobile ? "clamp(40px, 10vw, 56px)" : "clamp(70px, 5.8vw, 86px)",
                  color: "#f65151",
                  letterSpacing: "0",
                }}>
                  {t.hero.tagline2}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
